import { t } from "../i18n";
import {
  Check,
  ChevronDown,
  ChevronUp,
  Layers3,
  Plus,
  X
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type {
  Orders,
  PendingSubmission,
  Setup,
  ShopTable,
  TableMerge,
  TablePage
} from "../types";
import { PagesDialog } from "../components/PagesDialog";
import { useBackHandler } from "../backNavigation";
import {
  loadTableEmployeePreview,
  loadTableListView,
  loadTablePageId,
  loadTableSortMode,
  loadTableToolsOpen,
  saveTableEmployeePreview,
  saveTableListView,
  saveTablePageId,
  saveTableSortMode,
  saveTableToolsOpen
} from "../storage";
import { effectiveOrderLines, isOpenOrder, latestOpenOrder, pendingForTable } from "../utils";

type DisplayTable = ShopTable & {
  mergeId?: number;
  memberIds?: number[];
  primary?: boolean;
  /* Set on every cell of a merge whose members fill their whole rectangle. Those merges draw as
     one tile spanning the rectangle, like addMergedTableLabel on Android. */
  span?: { row: number; col: number; rows: number; columns: number };
};

type Props = {
  setup: Setup;
  orders: Orders | null;
  pending: PendingSubmission[];
  isAdmin: boolean;
  canMergeTables: boolean;
  canChangeTableLayout: boolean;
  onOpenTable: (label: string) => void;
  onCreateTemporaryTable: (label: string) => Promise<boolean>;
  onSaveSetup: (next: Setup) => void;
  onMoveTable: (tableId: number, row: number, col: number) => void;
  onMergeTables: (label: string, tableIds: number[]) => void;
  onExtendMerge: (mergeId: number, label: string, tableIds: number[]) => void;
  onSplitTable: (mergeId: number) => void;
  /* Set while an open order is being moved. The grid then acts as the picker, matching the
     Android app: only tables without an open order can be chosen. */
  changeTable: { orderId: number; sourceLabel: string } | null;
  onChangeTableTarget: (label: string) => void;
  onCancelChangeTable: () => void;
};

/* A merge keeps one cell per member, exactly like tablesForDisplay on Android. When the members
   fill their rectangle, the grid draws one tile across it. Otherwise the cells render as one
   connected shape: only the centre-most cell carries the label, and the rest draw blank.
   Collapsing an irregular merge into one tile would cover unrelated tables in its gaps. */
function mergedTables(merge: TableMerge, members: ShopTable[]): DisplayTable[] {
  if (!members.length) {
    return [];
  }
  const sorted = members
    .slice()
    .sort((a, b) => a.grid_row - b.grid_row || a.grid_col - b.grid_col);
  const minRow = Math.min(...sorted.map((table) => table.grid_row));
  const maxRow = Math.max(...sorted.map((table) => table.grid_row));
  const minCol = Math.min(...sorted.map((table) => table.grid_col));
  const maxCol = Math.max(...sorted.map((table) => table.grid_col));
  const centreRow = (minRow + maxRow) / 2;
  const centreCol = (minCol + maxCol) / 2;
  let primaryIndex = 0;
  let bestDistance = Number.POSITIVE_INFINITY;
  sorted.forEach((table, index) => {
    const distance =
      Math.abs(table.grid_row - centreRow) + Math.abs(table.grid_col - centreCol);
    if (distance < bestDistance) {
      bestDistance = distance;
      primaryIndex = index;
    }
  });
  const memberIds = sorted.map((table) => table.id);
  const rows = maxRow - minRow + 1;
  const columns = maxCol - minCol + 1;
  const cells = new Set(sorted.map((table) => `${table.grid_row}:${table.grid_col}`));
  const span = sorted.length > 1 && cells.size === sorted.length && cells.size === rows * columns
    ? { row: minRow, col: minCol, rows, columns }
    : undefined;
  return sorted.map((table, index) => ({
    ...table,
    id: -merge.id,
    label: merge.merged_label,
    page_id: merge.page_id || table.page_id,
    mergeId: merge.id,
    memberIds,
    primary: index === primaryIndex,
    span
  }));
}

export function TablesScreen({
  setup,
  orders,
  pending,
  isAdmin,
  canMergeTables,
  canChangeTableLayout,
  onOpenTable,
  onCreateTemporaryTable,
  onSaveSetup,
  onMoveTable,
  onMergeTables,
  onExtendMerge,
  onSplitTable,
  changeTable,
  onChangeTableTarget,
  onCancelChangeTable
}: Props) {
  const [addAt, setAddAt] = useState<{ row: number | null; col: number | null } | null>(null);
  const [newLabel, setNewLabel] = useState("");
  const [employeePreview, setEmployeePreview] = useState(loadTableEmployeePreview);
  const [listView, setListView] = useState(loadTableListView);
  const [tableSortMode, setTableSortMode] = useState(loadTableSortMode);
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSelection, setMergeSelection] = useState<number[]>([]);
  // A merged table tapped in merge mode: the waiter chooses between adding tables and splitting.
  const [mergeChoice, setMergeChoice] = useState<{ mergeId: number; label: string } | null>(null);
  // While set, the merge selection collects tables to add to this merge instead of a new one.
  const [extendMerge, setExtendMerge] = useState<{ mergeId: number; label: string } | null>(null);
  // Irregular merges draw one cell per member, so hovering any of them highlights them all.
  const [hoverMergeId, setHoverMergeId] = useState<number | null>(null);
  // Which selected table's number the merge keeps; null means the custom name below is used.
  const [mergeLabelChoice, setMergeLabelChoice] = useState<number | null>(null);
  const [mergeLabel, setMergeLabel] = useState<string | null>(null);
  const [manualOrder, setManualOrder] = useState<string | null>(null);
  const [pagesOpen, setPagesOpen] = useState(false);
  const [manageMode, setManageMode] = useState(false);
  const [selectedTables, setSelectedTables] = useState<number[]>([]);
  const [confirmDeleteTables, setConfirmDeleteTables] = useState<number[] | null>(null);
  const [dragTableId, setDragTableId] = useState<number | null>(null);
  const [dragOverCell, setDragOverCell] = useState<string | null>(null);
  const [toolsOpen, setToolsOpen] = useState(loadTableToolsOpen);
  const longPress = useRef<{ id: number; timer: number; x: number; y: number; moved: boolean } | null>(null);
  const tileTapReady = useRef(false);
  const adminControls = isAdmin && !employeePreview;
  /* canArrangeTables(): an admin outside employee preview, or a waiter holding the layout
     permission. Merged tables are never arrangeable. */
  const canArrange = isAdmin ? adminControls : canChangeTableLayout;
  const pages = useMemo(
    () => setup.table_pages
      .slice()
      .sort((a, b) => a.pos_row - b.pos_row || a.pos_col - b.pos_col || a.id - b.id),
    [setup.table_pages]
  );
  const fallbackPage: TablePage = { id: 0, name: t("Τραπέζια"), pos_row: 0, pos_col: 0 };
  const [pageId, setPageId] = useState(() => {
    const saved = loadTablePageId();
    return pages.find((page) => page.id === saved)?.id ?? (pages[0] ?? fallbackPage).id;
  });
  const swipeStart = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (pages.length && !pages.some((page) => page.id === pageId)) {
      const saved = loadTablePageId();
      setPageId(pages.find((page) => page.id === saved)?.id ?? pages[0].id);
      return;
    }
    if (pageId > 0) {
      saveTablePageId(pageId);
    }
  }, [pageId, pages]);

  useEffect(() => {
    saveTableToolsOpen(toolsOpen);
  }, [toolsOpen]);

  const currentPage = pages.find((page) => page.id === pageId) ?? pages[0] ?? fallbackPage;

  // Pages are arranged on a grid like external displays, so the neighbour in a direction is the
  // nearest page sharing the current row (or column), exactly like the Android app.
  const neighbourPage = (deltaRow: number, deltaCol: number): TablePage | undefined => {
    let best: TablePage | undefined;
    for (const page of pages) {
      if (page.id === currentPage.id) {
        continue;
      }
      if (deltaRow === 0) {
        if (page.pos_row !== currentPage.pos_row) {
          continue;
        }
        if (deltaCol > 0 && page.pos_col > currentPage.pos_col && (!best || page.pos_col < best.pos_col)) {
          best = page;
        } else if (deltaCol < 0 && page.pos_col < currentPage.pos_col && (!best || page.pos_col > best.pos_col)) {
          best = page;
        }
      } else {
        if (page.pos_col !== currentPage.pos_col) {
          continue;
        }
        if (deltaRow > 0 && page.pos_row > currentPage.pos_row && (!best || page.pos_row < best.pos_row)) {
          best = page;
        } else if (deltaRow < 0 && page.pos_row < currentPage.pos_row && (!best || page.pos_row > best.pos_row)) {
          best = page;
        }
      }
    }
    return best;
  };

  const handleSwipeEnd = (endX: number, endY: number): void => {
    const start = swipeStart.current;
    swipeStart.current = null;
    if (!start || pages.length < 2 || manageMode) {
      return;
    }
    const dx = endX - start.x;
    const dy = endY - start.y;
    const threshold = 60;
    let target: TablePage | undefined;
    if (Math.abs(dx) >= Math.abs(dy) && Math.abs(dx) > threshold) {
      target = neighbourPage(0, dx < 0 ? 1 : -1);
    } else if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > threshold) {
      target = neighbourPage(dy < 0 ? 1 : -1, 0);
    }
    if (target) {
      setPageId(target.id);
    }
  };

  const changingTable = Boolean(changeTable);
  /* Same rule as isAvailableTableChangeTarget() on Android: not the table it is already on,
     and not one that already holds an open order. */
  const isAvailableTarget = (label: string): boolean =>
    Boolean(changeTable)
    && label !== changeTable?.sourceLabel
    && !latestOpenOrder(orders, label);

  const labelOf = (tableId: number): string =>
    setup.shop_tables.find((table) => table.id === tableId)?.label ?? String(tableId);

  const toggleMergeSelection = (tableId: number): void => {
    setMergeSelection(
      mergeSelection.includes(tableId)
        ? mergeSelection.filter((id) => id !== tableId)
        : [...mergeSelection, tableId]
    );
  };

  const leaveMergeMode = (): void => {
    setMergeMode(false);
    setMergeSelection([]);
    setExtendMerge(null);
  };

  const handleTileTap = (table: DisplayTable): void => {
    if (changingTable) {
      if (isAvailableTarget(table.label)) {
        onChangeTableTarget(table.label);
      }
      return;
    }
    if (!mergeMode) {
      onOpenTable(table.label);
      return;
    }
    if (table.mergeId) {
      // Merged tables are never selected for another merge. Adding tables happens one merge at a time.
      if (!extendMerge) {
        setMergeChoice({ mergeId: table.mergeId, label: table.label });
      }
      return;
    }
    toggleMergeSelection(table.id);
  };

  const hasLeft = Boolean(neighbourPage(0, -1));
  const hasRight = Boolean(neighbourPage(0, 1));
  const hasUp = Boolean(neighbourPage(-1, 0));
  const hasDown = Boolean(neighbourPage(1, 0));

  /* Cells of an irregular merge grow half the grid gap toward their neighbours so they touch and
     read as one shape, and drop the radius on joined corners. Mirrors the leftMargin/width
     stretching in addTableGridCells. The gap is a CSS variable because phones use a smaller one. */
  const HALF_GAP = "calc(var(--table-gap) / -2)";
  const mergeJoinStyle = (table: DisplayTable): React.CSSProperties => {
    if (!table.mergeId || table.span) {
      return {};
    }
    const at = (row: number, col: number): boolean =>
      visible.some(
        (other) =>
          other.mergeId === table.mergeId && other.grid_row === row && other.grid_col === col
      );
    const left = at(table.grid_row, table.grid_col - 1);
    const right = at(table.grid_row, table.grid_col + 1);
    const up = at(table.grid_row - 1, table.grid_col);
    const down = at(table.grid_row + 1, table.grid_col);
    return {
      marginLeft: left ? HALF_GAP : undefined,
      marginRight: right ? HALF_GAP : undefined,
      marginTop: up ? HALF_GAP : undefined,
      marginBottom: down ? HALF_GAP : undefined,
      // Drop one of the two borders where cells meet, leaving a single divider line.
      borderLeftWidth: left ? 0 : undefined,
      borderTopWidth: up ? 0 : undefined,
      borderTopLeftRadius: left || up ? 0 : undefined,
      borderTopRightRadius: right || up ? 0 : undefined,
      borderBottomLeftRadius: left || down ? 0 : undefined,
      borderBottomRightRadius: right || down ? 0 : undefined
    };
  };

  const enterManageMode = (tableId: number): void => {
    if (!canArrange) {
      return;
    }
    setManageMode(true);
    setToolsOpen(true);
    leaveMergeMode();
    setSelectedTables([tableId]);
    if (navigator.vibrate) {
      navigator.vibrate(20);
    }
  };

  const exitManageMode = (): void => {
    setManageMode(false);
    setSelectedTables([]);
    setDragTableId(null);
    setDragOverCell(null);
  };

  useBackHandler(manageMode, exitManageMode, "page");
  useBackHandler(mergeMode, leaveMergeMode, "page");
  useBackHandler(manualOrder !== null, () => setManualOrder(null));
  useBackHandler(mergeLabel !== null, () => setMergeLabel(null));
  useBackHandler(Boolean(confirmDeleteTables), () => setConfirmDeleteTables(null));
  useBackHandler(Boolean(mergeChoice), () => setMergeChoice(null));
  useBackHandler(Boolean(addAt), () => setAddAt(null));

  const commitTables = (tables: ShopTable[]): void => {
    onSaveSetup({ ...setup, shop_tables: tables });
  };

  /* Drop on a free cell moves the table; drop on an occupied one swaps the two, matching
     moveTableTo / swapTablePositions. */
  const moveTableTo = (tableId: number, row: number, col: number): void => {
    const moving = setup.shop_tables.find((table) => table.id === tableId);
    if (!moving || (moving.grid_row === row && moving.grid_col === col)) {
      return;
    }
    onMoveTable(tableId, row, col);
  };

  const deleteTables = (ids: number[]): void => {
    commitTables(setup.shop_tables.map((table) =>
      ids.includes(table.id) ? { ...table, active: 0 } : table
    ));
    setSelectedTables([]);
    setConfirmDeleteTables(null);
  };

  const cellUnderPointer = (x: number, y: number): { row: number; col: number } | null => {
    const el = document.elementFromPoint(x, y)?.closest<HTMLElement>("[data-grid-cell]");
    if (!el?.dataset.gridCell) {
      return null;
    }
    const [row, col] = el.dataset.gridCell.split(":").map(Number);
    return { row, col };
  };

  const clearLongPress = (): void => {
    if (longPress.current) {
      window.clearTimeout(longPress.current.timer);
      longPress.current = null;
    }
  };

  const mergeSignature = (orders?.table_merges ?? [])
    .map((merge) => `${merge.id}:${merge.member_ids}`)
    .join("|");

  const addTable = (
    label: string,
    row: number | null,
    col: number | null,
    temporary = 0
  ): void => {
    const clean = label.trim();
    if (!clean) {
      return;
    }
    const active = setup.shop_tables.filter((table) => table.active);
    if (active.some((table) => table.label === clean)) {
      setAddAt(null);
      return;
    }
    let targetRow = row;
    let targetCol = col;
    if (targetRow === null || targetCol === null) {
      // No cell chosen: drop it in the first free slot of the current page, like the app does.
      const taken = new Set(
        active.filter((table) => table.page_id === pageId).map((table) => `${table.grid_row}:${table.grid_col}`)
      );
      outer: for (let r = 0; r < 40; r++) {
        for (let c = 0; c < 8; c++) {
          if (!taken.has(`${r}:${c}`)) {
            targetRow = r;
            targetCol = c;
            break outer;
          }
        }
      }
    }
    const revived = setup.shop_tables.find((table) => !table.active && table.label === clean);
    const nextTables = revived
      ? setup.shop_tables.map((table) =>
          table.id === revived.id
            ? { ...table, active: 1, temporary, page_id: pageId, grid_row: targetRow ?? 0, grid_col: targetCol ?? 0 }
            : table
        )
      : [
          ...setup.shop_tables,
          {
            id: setup.shop_tables.reduce((highest, table) => Math.max(highest, table.id), 0) + 1,
            label: clean,
            sort_order: setup.shop_tables.length,
            grid_row: targetRow ?? 0,
            grid_col: targetCol ?? 0,
            temporary,
            active: 1,
            page_id: pageId
          }
        ];
    onSaveSetup({ ...setup, shop_tables: nextTables });
    setAddAt(null);
    setNewLabel("");
  };

  /* "Δημιουργία παραγγελίας" accepts a table that does not exist yet. Android spawns a temporary
     table for it so the waiter can see and reopen it; the server retires that table again once
     the order is closed. This goes through its own endpoint rather than the setup patch, because
     setup writes are admin-only and waiters have to be able to do this. */
  const openManualOrder = async (label: string): Promise<void> => {
    const clean = label.trim();
    if (!clean) {
      return;
    }
    if (!setup.shop_tables.some((table) => table.active && table.label === clean)) {
      await onCreateTemporaryTable(clean);
    }
    onOpenTable(clean);
  };

  const displayTables = useMemo(() => {
    const active = setup.shop_tables.filter((table) => table.active);
    const hidden = new Set<number>();
    const merged: DisplayTable[] = [];
    for (const merge of orders?.table_merges ?? []) {
      const memberIds = merge.member_ids
        .split(",")
        .map((value) => Number.parseInt(value.trim(), 10))
        .filter((value) => value > 0);
      const members = active.filter((table) => memberIds.includes(table.id));
      const display = mergedTables(merge, members);
      if (display.length) {
        memberIds.forEach((id) => hidden.add(id));
        merged.push(...display);
      }
    }
    const unmerged: DisplayTable[] = active.filter((table) => !hidden.has(table.id));
    return [
      ...unmerged,
      ...merged
    ].sort((a, b) =>
      a.grid_row - b.grid_row ||
      a.grid_col - b.grid_col ||
      a.sort_order - b.sort_order ||
      a.id - b.id
    );
  }, [orders?.table_merges, setup.shop_tables]);

  const visible = displayTables.filter((table) =>
    pages.length ? table.page_id === pageId : true
  );

  const listTables = useMemo(() => {
    const numericValue = (label: string): number => {
      const clean = label.trim();
      return /^\d+$/.test(clean) ? Number.parseInt(clean, 10) : Number.POSITIVE_INFINITY;
    };
    const numericCompare = (first: DisplayTable, second: DisplayTable): number => {
      const firstNumber = numericValue(first.label);
      const secondNumber = numericValue(second.label);
      if (firstNumber !== secondNumber) {
        if (!Number.isFinite(firstNumber)) {
          return 1;
        }
        if (!Number.isFinite(secondNumber)) {
          return -1;
        }
        return firstNumber - secondNumber;
      }
      return first.label.localeCompare(second.label, "el", { sensitivity: "base" });
    };
    return visible
      .filter((table) => !table.mergeId || table.primary)
      .slice()
      .sort((first, second) => {
        if (tableSortMode === "open") {
          const firstOpen = Boolean(latestOpenOrder(orders, first.label));
          const secondOpen = Boolean(latestOpenOrder(orders, second.label));
          if (firstOpen !== secondOpen) {
            return firstOpen ? -1 : 1;
          }
        }
        return numericCompare(first, second);
      });
  }, [orders, tableSortMode, visible]);

  // Tables keep the exact grid position the admin arranged, and the grid scales to the screen
  // instead of scrolling: the bounding box of the page decides the number of rows and columns.
  const bounds = useMemo(() => {
    if (!visible.length) {
      return { minRow: 0, minCol: 0, rows: 1, columns: 1 };
    }
    let minRow = Number.POSITIVE_INFINITY;
    let minCol = Number.POSITIVE_INFINITY;
    let maxRow = Number.NEGATIVE_INFINITY;
    let maxCol = Number.NEGATIVE_INFINITY;
    for (const table of visible) {
      minRow = Math.min(minRow, table.grid_row);
      minCol = Math.min(minCol, table.grid_col);
      maxRow = Math.max(maxRow, table.grid_row);
      maxCol = Math.max(maxCol, table.grid_col);
    }
    // Admins get one spare row and column on every side so a table can be placed anywhere.
    const pad = adminControls && !listView && !mergeMode ? 1 : 0;
    return {
      minRow: minRow - pad,
      minCol: minCol - pad,
      rows: maxRow - minRow + 1 + pad * 2,
      columns: maxCol - minCol + 1 + pad * 2
    };
  }, [visible, adminControls, listView, mergeMode]);

  const occupied = useMemo(() => {
    const map = new Set<string>();
    for (const table of visible) {
      map.add(`${table.grid_row}:${table.grid_col}`);
    }
    return map;
  }, [visible]);

  const placeholders = useMemo(() => {
    if (!adminControls || listView || mergeMode) {
      return [];
    }
    const cells: { row: number; col: number }[] = [];
    for (let r = 0; r < bounds.rows; r++) {
      for (let c = 0; c < bounds.columns; c++) {
        const row = bounds.minRow + r;
        const col = bounds.minCol + c;
        if (!occupied.has(`${row}:${col}`)) {
          cells.push({ row, col });
        }
      }
    }
    return cells;
  }, [bounds, occupied, adminControls, listView, mergeMode]);

  return (
    <section
      className={["screen tables-screen", toolsOpen ? "tools-open" : ""].filter(Boolean).join(" ")}
      aria-labelledby="tables-heading"
    >
      <div className="screen-heading-row">
        <div>
          <h1 id="tables-heading">{changeTable ? t("Αλλαγή τραπεζιού") : t("Τραπέζια")}</h1>
          <p>
            {changeTable
              ? t("Από τραπέζι {0}", changeTable.sourceLabel)
              : t("{0} ανοιχτά", orders?.orders.filter(isOpenOrder).length ?? 0)}
          </p>
        </div>
        {changeTable ? null : (
          <button
            type="button"
            className="icon-button tables-tools-toggle"
            aria-expanded={toolsOpen}
            aria-label={toolsOpen ? t("Κλείσιμο ενεργειών τραπεζιών") : t("Άνοιγμα ενεργειών τραπεζιών")}
            title={toolsOpen ? t("Κλείσιμο ενεργειών τραπεζιών") : t("Άνοιγμα ενεργειών τραπεζιών")}
            onClick={() => setToolsOpen((open) => !open)}
          >
            {toolsOpen ? <ChevronUp /> : <ChevronDown />}
          </button>
        )}
      </div>
      {changeTable ? (
        <div className="table-change-bar">
          <button type="button" className="secondary-command wide" onClick={onCancelChangeTable}>
            <span>{t("Άκυρο")}</span>
          </button>
          <p className="table-change-hint">{t("Επίλεξε ένα τραπέζι χωρίς ανοιχτή παραγγελία.")}</p>
        </div>
      ) : null}
      <div className="tables-admin-bar" hidden={changingTable}>
          <div className={[
            "tables-tools-body",
            toolsOpen ? "open" : "",
            mergeMode || manageMode ? "contextual" : ""
          ].filter(Boolean).join(" ")}>
          <div className="tables-tools-content">
          <button
            type="button"
            className="primary-command wide"
            onClick={() => {
              setManualOrder("");
            }}
          >
            <Plus />
            <span>{t("Δημιουργία παραγγελίας")}</span>
          </button>
          {isAdmin ? (
            <button
              type="button"
              className={employeePreview ? "secondary-command wide accent" : "secondary-command wide"}
              onClick={() => {
                const next = !employeePreview;
                setEmployeePreview(next);
                saveTableEmployeePreview(next);
                // Switching role drops any half-finished merge, like toggleTableRoleView.
                leaveMergeMode();
              }}
            >
              <span>{t("Προβολή τραπεζιών: ")}{employeePreview ? t("Υπάλληλος") : t("Διαχειριστής")}</span>
            </button>
          ) : null}
          {adminControls ? (
            <div className="button-pair tables-structure-actions">
              <button type="button" className="secondary-command" onClick={() => {
                setAddAt({ row: null, col: null });
              }}>
                <span>{t("Προσθήκη τραπεζιού")}</span>
              </button>
              <button type="button" className="secondary-command" onClick={() => {
                setPagesOpen(true);
              }}>
                <span>{t("Σελίδες")}</span>
              </button>
            </div>
          ) : null}
          <div className="button-pair">
            <button
              type="button"
              className={listView ? "secondary-command" : "secondary-command accent"}
              onClick={() => {
                setListView(false);
                saveTableListView(false);
              }}
            >
              <span>{t("Πλέγμα")}</span>
            </button>
            <button
              type="button"
              className={listView ? "secondary-command accent" : "secondary-command"}
              onClick={() => {
                setListView(true);
                saveTableListView(true);
              }}
            >
              <span>{t("Λίστα")}</span>
            </button>
          </div>
          {listView ? (
            <div className="button-pair table-sort-actions" aria-label={t("Ταξινόμηση λίστας τραπεζιών")}>
              <button
                type="button"
                className={tableSortMode === "open" ? "secondary-command accent" : "secondary-command"}
                onClick={() => {
                  setTableSortMode("open");
                  saveTableSortMode("open");
                }}
              >
                <span>{t("Ανοιχτά πρώτα")}</span>
              </button>
              <button
                type="button"
                className={tableSortMode === "numeric" ? "secondary-command accent" : "secondary-command"}
                onClick={() => {
                  setTableSortMode("numeric");
                  saveTableSortMode("numeric");
                }}
              >
                <span>{t("Αριθμητικά")}</span>
              </button>
            </div>
          ) : null}
          {mergeMode ? (
            <div className="button-pair tables-contextual">
              {extendMerge ? (
                <button
                  type="button"
                  className="primary-command"
                  disabled={mergeSelection.length < 1}
                  onClick={() => {
                    onExtendMerge(extendMerge.mergeId, extendMerge.label, mergeSelection);
                    leaveMergeMode();
                  }}
                >
                  <span>{t("Προσθήκη ({0})", mergeSelection.length)}</span>
                </button>
              ) : (
                <button
                  type="button"
                  className="primary-command"
                  disabled={mergeSelection.length < 2}
                  onClick={() => {
                    // Default to keeping the first selected table's number, like the app does.
                    setMergeLabelChoice(mergeSelection[0]);
                    setMergeLabel(labelOf(mergeSelection[0]));
                  }}
                >
                  <span>{t("Συνένωση (")}{mergeSelection.length})</span>
                </button>
              )}
              <button
                type="button"
                className="secondary-command"
                onClick={leaveMergeMode}
              >
                <span>{t("Άκυρο")}</span>
              </button>
            </div>
          ) : canMergeTables && !manageMode ? (
            <button
              type="button"
              className="secondary-command wide"
              onClick={() => {
                setMergeMode(true);
                setMergeSelection([]);
                setToolsOpen(true);
              }}
            >
              <span>{t("Συνένωση τραπεζιών")}</span>
            </button>
          ) : null}
          {manageMode ? (
            <div className="tables-contextual">
              <div className="button-pair">
                <button type="button" className="secondary-command" onClick={exitManageMode}>
                  <span>{t("Τέλος διαχείρισης")}</span>
                </button>
                {selectedTables.length > 1 && adminControls ? (
                  <button
                    type="button"
                    className="secondary-command danger"
                    onClick={() => setConfirmDeleteTables(selectedTables)}
                  >
                    <span>{t("Διαγραφή επιλεγμένων")}</span>
                  </button>
                ) : null}
              </div>
              <p className="hint-text">
                {adminControls
                  ? t("Πάτημα επιλέγει. Κράτημα και σύρσιμο μετακινεί. Το κόκκινο − διαγράφει.")
                  : t("Κράτημα και σύρσιμο μετακινεί τραπέζι.")}
              </p>
            </div>
          ) : mergeMode ? (
            <p className="hint-text tables-contextual">
              {extendMerge
                ? t("Πάτημα επιλέγει τραπέζια για προσθήκη στο \"{0}\".", extendMerge.label)
                : t("Πάτημα επιλέγει τραπέζι. Πάτημα σε συνενωμένο τραπέζι δίνει επιλογές.")}
            </p>
          ) : null}
          </div>
          </div>
        </div>

      {listView ? (
        <div className="admin-list table-list">
          {/* One row per merge, not per member cell: the primary cell represents it in list view. */}
          {listTables.map((table) => {
            const openOrder = latestOpenOrder(orders, table.label);
            const hasUnservedItems = Boolean(
              openOrder && effectiveOrderLines(orders, openOrder.id).some((line) => !line.served)
            );
            return (
              <button
                type="button"
                className={[
                  "admin-row table-row",
                  openOrder ? "open" : "",
                  mergeMode && mergeSelection.includes(table.id) ? "selected" : "",
                  extendMerge && extendMerge.mergeId === table.mergeId ? "selected" : ""
                ].filter(Boolean).join(" ")}
                key={table.mergeId ? `merge-${table.mergeId}` : table.id}
                onClick={() => handleTileTap(table)}
              >
                <div className="admin-row-copy">
                  <strong>{t("Τραπέζι ")}{table.label}</strong>
                </div>
                {hasUnservedItems ? (
                  <span className="table-attention-badge" aria-hidden="true">!</span>
                ) : null}
              </button>
            );
          })}
          {!visible.length ? <div className="empty-state">{t("Δεν υπάρχουν τραπέζια σε αυτή τη σελίδα.")}</div> : null}
        </div>
      ) : (
      /* Edge dots mark which directions hold another page, exactly like the Android grid. */
      <div
        className={[
          "table-grid-wrap",
          hasLeft ? "gutter-left" : "",
          hasRight ? "gutter-right" : "",
          hasUp ? "gutter-up" : "",
          hasDown ? "gutter-down" : ""
        ].filter(Boolean).join(" ")}
      >
        {hasLeft ? <span className="page-edge-dot left" aria-hidden="true" /> : null}
        {hasRight ? <span className="page-edge-dot right" aria-hidden="true" /> : null}
        {hasUp ? <span className="page-edge-dot up" aria-hidden="true" /> : null}
        {hasDown ? <span className="page-edge-dot down" aria-hidden="true" /> : null}
      <div
        /* Remount the grid whenever the merge set changes. Splitting rewrites every member
           cell's inline margins and borders at once, and Chrome on Android can leave the old
           composited paint behind — which is why the tiles looked wrong until a tab change
           forced a new subtree. A fresh key guarantees a clean one. */
        key={mergeSignature}
        className="table-grid"
        style={{
          gridTemplateColumns: `repeat(${bounds.columns}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${bounds.rows}, minmax(0, 1fr))`
        }}
        onTouchStart={(event) => {
          const touch = event.touches[0];
          swipeStart.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
        }}
        onTouchEnd={(event) => {
          const touch = event.changedTouches[0];
          if (touch) {
            handleSwipeEnd(touch.clientX, touch.clientY);
          }
        }}
        onPointerDown={(event) => {
          if (event.pointerType === "mouse") {
            swipeStart.current = { x: event.clientX, y: event.clientY };
          }
        }}
        onPointerUp={(event) => {
          if (event.pointerType === "mouse") {
            handleSwipeEnd(event.clientX, event.clientY);
          }
        }}
      >
        {visible.map((table) => {
          // A merge filling its rectangle is one tile; its other member cells are covered by it.
          if (table.span && !table.primary) {
            return null;
          }
          const openOrder = latestOpenOrder(orders, table.label);
          const hasUnservedItems = Boolean(
            openOrder && effectiveOrderLines(orders, openOrder.id).some((line) => !line.served)
          );
          const tablePending = pendingForTable(pending, orders, table.label);
          const placement = table.span
            ? {
                gridColumn: `${table.span.col - bounds.minCol + 1} / span ${table.span.columns}`,
                gridRow: `${table.span.row - bounds.minRow + 1} / span ${table.span.rows}`
              }
            : {
                gridColumn: table.grid_col - bounds.minCol + 1,
                gridRow: table.grid_row - bounds.minRow + 1
              };
          return (
            <button
              type="button"
              key={table.mergeId ? `merge-${table.mergeId}-${table.grid_row}-${table.grid_col}` : table.id}
              className={[
                "table-tile",
                openOrder ? "open" : "",
                tablePending ? `pending ${tablePending.state}` : "",
                table.mergeId ? "merged" : "",
                table.mergeId && !table.primary ? "merged-secondary" : "",
                changingTable && !isAvailableTarget(table.label) ? "change-unavailable" : "",
                mergeMode && mergeSelection.includes(table.id) ? "selected" : "",
                extendMerge && extendMerge.mergeId === table.mergeId ? "selected" : "",
                manageMode && selectedTables.includes(table.id) ? "selected" : "",
                table.mergeId && hoverMergeId === table.mergeId ? "hover" : "",
                dragTableId === table.id ? "dragging" : "",
                dragOverCell === `${table.grid_row}:${table.grid_col}` ? "drag-over" : ""
              ].filter(Boolean).join(" ")}
              /* A spanning merge is never a drop target, so a drag cannot reshape it by accident. */
              data-grid-cell={table.span ? undefined : `${table.grid_row}:${table.grid_col}`}
              onPointerEnter={(event) => {
                if (event.pointerType === "mouse" && table.mergeId && !table.span) {
                  setHoverMergeId(table.mergeId);
                }
              }}
              onPointerLeave={() => {
                if (table.mergeId) {
                  setHoverMergeId(null);
                }
              }}
              onClick={(event) => {
                const keyboardClick = event.detail === 0;
                if (manageMode || (!keyboardClick && !tileTapReady.current)) {
                  return;
                }
                tileTapReady.current = false;
                handleTileTap(table);
              }}
              /* Chrome's own long-press (context menu / text selection) fires around the same
                 500ms and cancels the pointer stream, which would swallow the gesture. */
              onContextMenu={(event) => event.preventDefault()}
              onPointerDown={(event) => {
                tileTapReady.current = false;
                // Always record the press so pointerup can tell a tap from a swipe; only a
                // real, arrangeable table also arms the long-press timer.
                const armsLongPress = canArrange && !manageMode && !table.mergeId;
                if (manageMode && !table.mergeId) {
                  event.currentTarget.setPointerCapture(event.pointerId);
                }
                const id = table.id;
                longPress.current = {
                  id,
                  timer: armsLongPress ? window.setTimeout(() => enterManageMode(id), 500) : 0,
                  x: event.clientX,
                  y: event.clientY,
                  moved: false
                };
              }}
              onPointerMove={(event) => {
                const state = longPress.current;
                if (!state) {
                  return;
                }
                const far =
                  Math.abs(event.clientX - state.x) > 8 || Math.abs(event.clientY - state.y) > 8;
                if (!manageMode) {
                  // Moving cancels the long press so a swipe never opens management mode,
                  // and marks the gesture so pointerup does not treat it as a tap.
                  if (far && !state.moved) {
                    state.moved = true;
                    window.clearTimeout(state.timer);
                  }
                  return;
                }
                if (!state.moved && far) {
                  state.moved = true;
                  setDragTableId(state.id);
                }
                if (state.moved) {
                  const cell = cellUnderPointer(event.clientX, event.clientY);
                  setDragOverCell(cell ? `${cell.row}:${cell.col}` : null);
                }
              }}
              onPointerUp={(event) => {
                const state = longPress.current;
                clearLongPress();
                if (manageMode && state?.moved) {
                  tileTapReady.current = false;
                  const cell = cellUnderPointer(event.clientX, event.clientY);
                  if (cell) {
                    moveTableTo(state.id, cell.row, cell.col);
                  }
                  setDragTableId(null);
                  setDragOverCell(null);
                  return;
                }
                setDragTableId(null);
                setDragOverCell(null);
                if (manageMode) {
                  tileTapReady.current = false;
                  if (adminControls && !table.mergeId && !state?.moved) {
                    setSelectedTables((current) =>
                      current.includes(table.id)
                        ? current.filter((id) => id !== table.id)
                        : [...current, table.id]
                    );
                  }
                  return;
                }
                // A drag that began on this tile is a page swipe, not a tap on the table.
                tileTapReady.current = Boolean(state && !state.moved);
              }}
              onPointerCancel={() => {
                tileTapReady.current = false;
                clearLongPress();
                setDragTableId(null);
                setDragOverCell(null);
              }}
              style={{
                ...placement,
                ...mergeJoinStyle(table)
              }}
            >
              {manageMode && adminControls && !table.mergeId ? (
                <span
                  className="table-delete-badge"
                  role="button"
                  aria-label={t("Διαγραφή {0}", table.label)}
                  onPointerDown={(event) => event.stopPropagation()}
                  onPointerUp={(event) => {
                    event.stopPropagation();
                    clearLongPress();
                    setConfirmDeleteTables([table.id]);
                  }}
                  onClick={(event) => event.stopPropagation()}
                >
                  −
                </span>
              ) : null}
              {table.mergeId && table.primary ? <Layers3 className="table-corner-icon" /> : null}
              {!table.mergeId || table.primary ? <strong>{table.label}</strong> : null}
              {hasUnservedItems && (!table.mergeId || table.primary) && !manageMode ? (
                <span className="table-attention-badge" aria-hidden="true">!</span>
              ) : null}
            </button>
          );
        })}
        {placeholders.map((cell) => (
          <button
            type="button"
            className={[
              "table-tile placeholder",
              dragOverCell === `${cell.row}:${cell.col}` ? "drag-over" : ""
            ].filter(Boolean).join(" ")}
            key={`empty-${cell.row}-${cell.col}`}
            data-grid-cell={`${cell.row}:${cell.col}`}
            style={{
              gridColumn: cell.col - bounds.minCol + 1,
              gridRow: cell.row - bounds.minRow + 1
            }}
            onClick={() => {
              setNewLabel("");
              setAddAt({ row: cell.row, col: cell.col });
            }}
            aria-label={t("Προσθήκη τραπεζιού")}
          >
            <Plus />
          </button>
        ))}
      </div>
      </div>
      )}

      {!visible.length && !adminControls && !listView ? (
        <div className="empty-state">{t("Δεν υπάρχουν τραπέζια σε αυτή τη σελίδα.")}</div>
      ) : null}

      {manualOrder !== null ? (
        <div className="dialog-backdrop">
          <section className="details-dialog" role="dialog" aria-modal="true" aria-label={t("Δημιουργία παραγγελίας")}>
            <header className="dialog-header">
              <div><h2>{t("Δημιουργία παραγγελίας")}</h2></div>
              <button type="button" className="icon-button" onClick={() => setManualOrder(null)} aria-label={t("Κλείσιμο")}>
                <X />
              </button>
            </header>
            <div className="dialog-scroll">
              <label className="field">
                <span>{t("Αριθμός τραπεζιού")}</span>
                <input value={manualOrder} onChange={(event) => setManualOrder(event.target.value)} autoFocus />
              </label>
            </div>
            <footer className="dialog-actions">
              <button type="button" className="secondary-command" onClick={() => setManualOrder(null)}>
                <span>{t("Άκυρο")}</span>
              </button>
              <button
                type="button"
                className="primary-command"
                disabled={!manualOrder.trim()}
                onClick={() => {
                  const label = manualOrder.trim();
                  setManualOrder(null);
                  void openManualOrder(label);
                }}
              >
                <span>{t("Άνοιγμα")}</span>
              </button>
            </footer>
          </section>
        </div>
      ) : null}

      {mergeLabel !== null ? (
        <div className="dialog-backdrop">
          <section className="details-dialog" role="dialog" aria-modal="true" aria-label={t("Συνένωση τραπεζιών")}>
            <header className="dialog-header">
              <div>
                <h2>{t("Συνένωση τραπεζιών")}</h2>
                <p>{t("Τα τραπέζια θα εμφανίζονται ως ένα μέχρι να γίνει διαχωρισμός.")}</p>
              </div>
              <button type="button" className="icon-button" onClick={() => setMergeLabel(null)} aria-label={t("Κλείσιμο")}>
                <X />
              </button>
            </header>
            <div className="dialog-scroll">
              <p className="hint-text">{t("Διάλεξε ποιος αριθμός θα κρατηθεί για το νέο τραπέζι ή βάλε προσαρμοσμένο όνομα.")}</p>
              <div className="option-group check-grid">
                {mergeSelection.map((id) => (
                  <label className="check-option" key={id}>
                    <input
                      type="radio"
                      name="merge-label-choice"
                      checked={mergeLabelChoice === id}
                      onChange={() => {
                        setMergeLabelChoice(id);
                        setMergeLabel(labelOf(id));
                      }}
                    />
                    <span>{t("Τραπέζι ")}{labelOf(id)}</span>
                  </label>
                ))}
                <label className="check-option">
                  <input
                    type="radio"
                    name="merge-label-choice"
                    checked={mergeLabelChoice === null}
                    onChange={() => {
                      setMergeLabelChoice(null);
                      setMergeLabel("");
                    }}
                  />
                  <span>{t("Custom όνομα")}</span>
                </label>
              </div>
              <label className="field">
                <span>{t("Όνομα συνενωμένου τραπεζιού")}</span>
                <input
                  value={mergeLabel}
                  disabled={mergeLabelChoice !== null}
                  onChange={(event) => setMergeLabel(event.target.value)}
                  onFocus={() => setMergeLabelChoice(null)}
                />
              </label>
            </div>
            <footer className="dialog-actions">
              <button type="button" className="secondary-command" onClick={() => setMergeLabel(null)}>
                <span>{t("Άκυρο")}</span>
              </button>
              <button
                type="button"
                className="primary-command"
                disabled={!mergeLabel.trim()}
                onClick={() => {
                  onMergeTables(mergeLabel.trim(), mergeSelection);
                  setMergeLabel(null);
                  setMergeMode(false);
                  setMergeSelection([]);
                }}
              >
                <span>{t("Συνένωση")}</span>
              </button>
            </footer>
          </section>
        </div>
      ) : null}

      {confirmDeleteTables ? (
        <div className="dialog-backdrop">
          <section className="permission-dialog" role="dialog" aria-modal="true" aria-label={t("Διαγραφή τραπεζιών")}>
            <header>
              <h2>{t("Διαγραφή τραπεζιών")}</h2>
              <p>
                {confirmDeleteTables.length > 1
                  ? t("Να διαγραφούν {0} τραπέζια;", confirmDeleteTables.length)
                  : t("Να διαγραφεί το τραπέζι \"{0}\";", labelOf(confirmDeleteTables[0]))}
              </p>
            </header>
            <div className="dialog-actions">
              <button type="button" className="secondary-command" onClick={() => setConfirmDeleteTables(null)}>
                <span>{t("Άκυρο")}</span>
              </button>
              <button type="button" className="primary-command" onClick={() => deleteTables(confirmDeleteTables)}>
                <span>{t("Διαγραφή")}</span>
              </button>
            </div>
          </section>
        </div>
      ) : null}

      {mergeChoice ? (
        <div className="dialog-backdrop">
          <section className="permission-dialog" role="dialog" aria-modal="true" aria-label={t("Συνενωμένο τραπέζι")}>
            <header>
              <h2>{t("Συνενωμένο τραπέζι \"{0}\"", mergeChoice.label)}</h2>
              <p>{t("Πρόσθεσε τραπέζια σε αυτή τη συνένωση ή διαχώρισέ τη στα αρχικά τραπέζια.")}</p>
            </header>
            {/* Nothing changes until one of the two actions is chosen. */}
            <div className="dialog-actions merge-choice-actions">
              <button type="button" className="secondary-command" onClick={() => setMergeChoice(null)}>
                <span>{t("Άκυρο")}</span>
              </button>
              <button
                type="button"
                className="secondary-command"
                onClick={() => {
                  onSplitTable(mergeChoice.mergeId);
                  setMergeChoice(null);
                  leaveMergeMode();
                }}
              >
                <span>{t("Διαχωρισμός")}</span>
              </button>
              <button
                type="button"
                className="primary-command"
                onClick={() => {
                  setExtendMerge(mergeChoice);
                  setMergeSelection([]);
                  setMergeChoice(null);
                }}
              >
                <span>{t("Προσθήκη τραπεζιών")}</span>
              </button>
            </div>
          </section>
        </div>
      ) : null}

      {pagesOpen ? (
        <PagesDialog setup={setup} onClose={() => setPagesOpen(false)} onSaveSetup={onSaveSetup} />
      ) : null}

      {addAt ? (
        <div className="dialog-backdrop">
          <section className="details-dialog" role="dialog" aria-modal="true" aria-label={t("Νέο τραπέζι")}>
            <header className="dialog-header">
              <div>
                <h2>{t("Νέο τραπέζι")}</h2>
                <p>{currentPage.name || t("Τραπέζια")}</p>
              </div>
              <button type="button" className="icon-button" onClick={() => setAddAt(null)} aria-label={t("Κλείσιμο")}>
                <X />
              </button>
            </header>
            <div className="dialog-scroll">
              <label className="field">
                <span>{t("Αριθμός / όνομα τραπεζιού")}</span>
                <input value={newLabel} onChange={(event) => setNewLabel(event.target.value)} autoFocus />
              </label>
            </div>
            <footer className="dialog-actions">
              <button type="button" className="secondary-command" onClick={() => setAddAt(null)}>
                <span>{t("Άκυρο")}</span>
              </button>
              <button
                type="button"
                className="primary-command"
                disabled={!newLabel.trim()}
                onClick={() => addTable(newLabel, addAt.row, addAt.col)}
              >
                <Check />
                <span>{t("Αποθήκευση")}</span>
              </button>
            </footer>
          </section>
        </div>
      ) : null}
    </section>
  );
}
